import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * Message List Controller
 * 
 * A reusable Stimulus controller for message list scroll management,
 * infinite scrolling, and new message handling.
 * 
 * Usage:
 *   <div data-controller="message-list"
 *        data-message-list-auto-scroll-value="true"
 *        data-message-list-load-more-url-value="/messages/load_more">
 *     <div data-message-list-target="list">
 *       <!-- Messages here -->
 *     </div>
 *     <div data-message-list-target="loadTrigger" class="d-none">
 *       Loading more...
 *     </div>
 *   </div>
 */
export default class extends ApplicationController {
  static targets = ["list", "loadTrigger", "newIndicator", "firstUnread"]
  static values = {
    autoScroll: { type: Boolean, default: true },
    scrollThreshold: { type: Number, default: 100 },
    loadMoreUrl: String,
    page: { type: Number, default: 1 },
    hasMore: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    currentUserId: Number
  }

  connect() {
    Logger.debug('MessageListController connecting', { 
      autoScroll: this.autoScrollValue,
      loadMoreUrl: this.loadMoreUrlValue
    })
    this.setupScrollListener()
    this.setupIntersectionObserver()
    this.setupTurboStreamListener()
    this.setupVisibilityObserver()
    this.setupMutationObserver() 
    this.refreshMessageGrouping() 
    this.refreshDateDividers()   
    this.scrollToBottom()
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldRefresh = false
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') shouldRefresh = true
      })
      if (shouldRefresh) this.refreshMessageGrouping()
    })
    
    const target = this.hasListTarget ? this.listTarget : this.element
    this.mutationObserver.observe(target, { childList: true, subtree: true })
  }

  refreshMessageGrouping() {
    const messages = this.element.querySelectorAll('.message-row')
    messages.forEach((msg, index) => {
      const prev = messages[index - 1]
      let isGrouped = false
      
      if (prev) {
        const sameUser = msg.dataset.userId === prev.dataset.userId
        const timeDiff = parseInt(msg.dataset.createdAt) - parseInt(prev.dataset.createdAt)
        const withinFiveMins = timeDiff < 300 // 5 minutes
        
        if (sameUser && withinFiveMins) {
          isGrouped = true
        }
      }
      
      if (isGrouped) {
        msg.classList.add('message-grouped')
      } else {
        msg.classList.remove('message-grouped')
      }
    })
  }
  refreshDateDividers() {
    const messages = this.element.querySelectorAll('.message-row')
    let lastDate = null
    
    messages.forEach(msg => {
      const currentDate = msg.dataset.date
      if (!currentDate) return
      
      const prev = msg.previousElementSibling
      const hasDivider = prev && prev.classList.contains('date-divider')
      
      if (currentDate !== lastDate) {
        if (!hasDivider) {
          this.insertDateDivider(msg)
        }
        lastDate = currentDate
      } else if (hasDivider) {
        // Remove redundant divider if it somehow exists
        prev.remove()
      }
    })
  }

  disconnect() {
    this.cleanup()
  }

  // === Scroll Management ===
  
  setupScrollListener() {
    this.handleScroll = this.handleScroll.bind(this)
    this.scrollContainer.addEventListener("scroll", this.handleScroll, { passive: true })
  }

  handleScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer
    
    // Check if user is near bottom
    this.isAtBottom = scrollHeight - scrollTop - clientHeight < this.scrollThresholdValue
    
    // Hide new message indicator if at bottom
    if (this.isAtBottom && this.hasNewIndicatorTarget) {
      this.newIndicatorTarget.classList.add("d-none")
    }
    
    // Check for infinite scroll trigger
    if (scrollTop < 200 && this.hasMoreValue && !this.loadingValue) {
      this.loadMore()
    }
  }

  scrollToBottom() {
    if (!this.autoScrollValue) return
    
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.scrollContainer.scrollTop = this.scrollContainer.scrollHeight
        this.isAtBottom = true
      })
    }, 100);
  }

  scrollToMessage(messageId) {
    const message = this.element.querySelector(`[data-message-id="${messageId}"]`)
    if (message) {
      message.scrollIntoView({ behavior: "smooth", block: "center" })
      message.classList.add("highlight")
      setTimeout(() => message.classList.remove("highlight"), 2000)
    }
  }

  get scrollContainer() {
    return this.hasListTarget ? this.listTarget : this.element
  }

  // === Visibility Observer ===
  
  setupVisibilityObserver() {
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // When the element becomes visible (e.g. modal opens, tab switches)
          this.scrollToBottom()
          this.markVisibleMessagesAsRead()
        }
      })
    }, { threshold: 0.1 })
    this.visibilityObserver.observe(this.element)

    // Observe individual messages for marking as read
    this.messageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const messageId = entry.target.dataset.messageId
          if (messageId) this.markAsRead(messageId)
          this.messageObserver.unobserve(entry.target)
        }
      })
    }, { threshold: 0.9 })
  }

  markVisibleMessagesAsRead() {
    this.element.querySelectorAll('[data-message-id]').forEach(msg => {
      this.messageObserver.observe(msg)
    })
  }

  async markAsRead(messageId) {
    const messageEl = this.element.querySelector(`[id="message_${messageId}"]`)
    if (!messageEl) return
    
    // Only mark if it's not our own message
    const userId = messageEl.dataset.userId
    if (userId && parseInt(userId) === this.currentUserIdValue) return
    
    // Check if it's already marked as read
    if (messageEl.querySelector('.fa-check-double.text-success')) return
    
    try {
      await this.safeFetch(`/messages/${messageId}/mark_read`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      // Dispatch event for other controllers (like messaging-nav) to sync unread state
      this.dispatch('message:read', {
        detail: {
          messageId: messageId,
          messageableId: this.element.dataset.contextId,
          messageableType: this.element.dataset.contextType
        },
        bubbles: true
      })
    } catch (e) {
      // Ignore errors for mark_read
    }
  }

  // === Infinite Scroll ===
  
  setupIntersectionObserver() {
    if (!this.hasLoadTriggerTarget || !this.hasLoadMoreUrlValue) return
    
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && this.hasMoreValue && !this.loadingValue) {
          this.loadMore()
        }
      },
      { threshold: 0.1 }
    )
    
    this.observer.observe(this.loadTriggerTarget)
  }

  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue || !this.hasLoadMoreUrlValue) return
    
    this.loadingValue = true
    if (this.hasLoadTriggerTarget) {
      this.loadTriggerTarget.classList.remove("d-none")
    }
    
    try {
      const url = new URL(this.loadMoreUrlValue, window.location.origin)
      url.searchParams.set("page", this.pageValue + 1)
      
      const response = await this.safeFetch(url, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (response.ok) {
        const contentType = response.headers.get("Content-Type")
        const html = await response.text()
        
        // Save scroll position before prepending
        const oldScrollHeight = this.scrollContainer.scrollHeight
        
        if (contentType && contentType.includes("turbo-stream")) {
          // Let Turbo handle the stream
          Turbo.renderStreamMessage(html)
        } else if (this.hasListTarget) {
          // Manually prepend HTML
          this.listTarget.insertAdjacentHTML('afterbegin', html.trim())
        }
        
        // Restore scroll position
        requestAnimationFrame(() => {
          const newScrollHeight = this.scrollContainer.scrollHeight
          this.scrollContainer.scrollTop = newScrollHeight - oldScrollHeight
        })
        
        this.pageValue++
        
        // Check if there are more messages without full DOM parsing
        if (html.includes('data-has-more="false"')) {
          this.hasMoreValue = false
        } else if (html.trim().length === 0) {
          this.hasMoreValue = false
        }
      }
    } catch (error) {
      Logger.error('Error loading more messages', error)
    } finally {
      this.loadingValue = false
      if (this.hasLoadTriggerTarget) {
        this.loadTriggerTarget.classList.add("d-none")
      }
    }
  }

  // === Turbo Stream Handling ===
  
  setupTurboStreamListener() {
    this.handleTurboStream = this.handleTurboStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this.handleTurboStream)
  }

  handleTurboStream(event) {
    const streamElement = event.detail.newStream
    const targetId = streamElement.target
    
    // Check if this is a message append for our list
    if (streamElement.action === "append" && this.isOurTarget(targetId)) {
      Logger.debug('Intercepting turbo stream append', { targetId })
      
      // If user is at bottom, scroll after render
      const originalRender = event.detail.render
      event.detail.render = async (stream) => {
        if (originalRender) {
          await originalRender(stream)
        } else {
          await stream.performAction()
        }
        
        // Ensure date divider for the newly added message
        this.checkDateDivider(stream.targetElement)
        
        if (this.isAtBottom) {
          this.scrollToBottom()
        } else {
          this.showNewIndicator()
        }
        this.markVisibleMessagesAsRead()
      }
    }
  }

  checkDateDivider(container) {
    if (!container) return
    const messages = container.querySelectorAll('.message-row')
    if (messages.length === 0) return
    
    // We only care about the last one (the newest) for appends
    const lastMessage = messages[messages.length - 1]
    const prevMessage = lastMessage.previousElementSibling
    
    if (!prevMessage || !prevMessage.classList.contains('message-row')) {
      // If it's the first message and no divider above it
      if (!lastMessage.previousElementSibling || !lastMessage.previousElementSibling.classList.contains('date-divider')) {
        this.insertDateDivider(lastMessage)
      }
      return
    }
    
    const lastDate = lastMessage.dataset.date
    const prevDate = prevMessage.dataset.date
    
    if (lastDate && prevDate && lastDate !== prevDate) {
      this.insertDateDivider(lastMessage)
    }
  }

  insertDateDivider(messageEl) {
    const dateStr = messageEl.dataset.date
    if (!dateStr) return
    
    const date = new Date(dateStr + 'T00:00:00')
    const today = new Date()
    today.setHours(0,0,0,0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    let label = date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    if (date.getTime() === today.getTime()) label = "Today"
    if (date.getTime() === yesterday.getTime()) label = "Yesterday"
    
    const divider = document.createElement('div')
    divider.className = 'date-divider'
    divider.innerHTML = `<span class="date-divider-text">${label}</span>`
    
    messageEl.parentNode.insertBefore(divider, messageEl)
  }

  isOurTarget(targetId) {
    // Check if the turbo stream targets our message list
    // We standardized on "messages-container" but also support context-specific IDs
    return targetId === "messages-container" || 
           this.element.querySelector(`#${targetId}`) !== null ||
           this.element.id === targetId ||
           targetId === "messages"
  }

  showNewIndicator() {
    if (this.hasNewIndicatorTarget) {
      this.newIndicatorTarget.classList.remove("d-none")
    }
  }

  // Jump to newest message (when clicking on indicator)
  jumpToNewest() {
    this.scrollToBottom()
    if (this.hasNewIndicatorTarget) {
      this.newIndicatorTarget.classList.add("d-none")
    }
  }

  // === Cleanup ===
  
  cleanup() {
    this.scrollContainer.removeEventListener("scroll", this.handleScroll)
    document.removeEventListener("turbo:before-stream-render", this.handleTurboStream)
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }
  }
}